/** Custom AG-Grid column header (Slice 6, D12 Affinity parity):
 *  - click the label → a field EXPLAINER popover (name · human-readable type · origin chip)
 *  - a ⋮ button → the column-actions menu (Edit field · Sort A→Z / Z→A · Filter · Pin · Insert
 *    left/right · Hide · Delete field), gated by the field's origin + the list kind
 *  - renders the active sort arrow itself (a custom header replaces AG's built-in one)
 *
 * AG still owns column RESIZE (its handle sits outside this component) and DRAG-reorder (initiated on
 * the header cell wrapper), so those keep working. App-side actions (rename/delete/hide/insert) come
 * in via the grid `context.headerActions`; AG-native ones (sort/filter) use params.setSort / the api.
 */
import { useEffect, useState } from 'react';
import type { IHeaderParams } from 'ag-grid-community';
import styles from './crm.module.css';
import { CrmPopover } from './CrmPopover';
import { SYSTEM_FAMILY_LABELS } from './crmSystemFields';

// human-readable, operator-facing type copy (E7.1). Keyed by crm_field_def.field_type.
export const FIELD_TYPE_EXPLAINERS: Record<string, string> = {
  text: 'Single-line text',
  long_text: 'Multi-line text',
  number: 'Number',
  currency: 'Currency amount',
  date: 'Date',
  enum: 'Single-select dropdown',
  multi_enum: 'Multi-select dropdown',
  ranked_enum: 'Ranked single-select (Status / Stage)',
  person: 'Linked person',
  person_multi: 'Linked people',
  organization: 'Linked organization',
  organization_multi: 'Linked organizations',
};

// the field payload the edit dock needs (co-located here so CrmFieldEditPanel can import it without
// a circular dep — the panel already imports FIELD_TYPE_EXPLAINERS from this module).
export interface EditField {
  fieldKey: string;
  label: string;
  fieldType: string;
  origin?: string | null;
}

export interface HeaderActions {
  isNative: boolean;
  openFieldEdit: (field: EditField) => void;
  deleteField: (fieldKey: string, label: string) => void;
  hideColumn: (fieldKey: string) => void;
  insertColumn: (fieldKey: string, side: 'left' | 'right') => void;
}

// per-column static info threaded via headerComponentParams; actions via the live grid context.
type Params = IHeaderParams & {
  origin?: string | null;
  fieldType?: string;
  fieldKey?: string;
  // W5 (§9.2 / §7.4): the system-field metadata this header reads.
  //
  // ⚠ `systemFamily` — catalog MEMBERSHIP — decides the rename/delete affordance, and NOT `origin`,
  // which is 'system' for a catalog column AND for the stored, editable `name` column on every
  // opportunity list. It is deliberately not `read_only` either: an earlier cut passed a `readOnly`
  // prop and documented it as the deciding capability while the code branched on `systemFamily`, so
  // the stated contract and the implemented one were different predicates and the prop was dead
  // (review finding 13). Membership is the RIGHT predicate here, not merely the shipped one: rename
  // and delete act on a stored `crm_field_def` row, which a catalog field does not have, so
  // `update_field` would have nothing to rename regardless of whether the field were writable. A
  // hypothetical `read_only: false` system field must still not offer them.
  systemFamily?: string | null;
  definition?: string | null;
};

export function CrmColumnHeader(params: Params) {
  const { displayName, column } = params;
  const origin = params.origin ?? null;
  const fieldType = params.fieldType ?? 'text';
  const fieldKey = params.fieldKey ?? column.getColId();
  const actions = (params.context as { headerActions?: HeaderActions } | undefined)?.headerActions;

  const [sort, setSortDir] = useState<'asc' | 'desc' | null>(column.getSort() ?? null);
  // Item A (multi-sort): this column's precedence (0-based) + whether MORE than one column is sorted
  // (drives the priority number) + whether any OTHER column is sorted (drives the "Add to sort" items).
  const [sortIndex, setSortIndex] = useState<number | null>(column.getSortIndex?.() ?? null);
  const [multiActive, setMultiActive] = useState(false);
  const [otherSorted, setOtherSorted] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [explainAnchor, setExplainAnchor] = useState<HTMLElement | null>(null);

  // reflect the live sort state (a saved view, the Sort panel, or a menu action changes it out of
  // band). The GRID-level sortChanged fires on ANY column's change, so every header's priority number
  // stays correct when another column is added/removed/reordered; the per-column event is a belt-and-
  // suspenders for an apply that touches only this column.
  useEffect(() => {
    const refresh = () => {
      setSortDir(column.getSort() ?? null);
      setSortIndex(column.getSortIndex?.() ?? null);
      const state = params.api.getColumnState?.() ?? [];
      const sorted = state.filter((s) => s.sort === 'asc' || s.sort === 'desc');
      setMultiActive(sorted.length > 1);
      setOtherSorted(sorted.some((s) => s.colId !== column.getColId()));
    };
    refresh();
    params.api.addEventListener('sortChanged', refresh);
    column.addEventListener('sortChanged', refresh);
    return () => {
      params.api.removeEventListener('sortChanged', refresh);
      column.removeEventListener('sortChanged', refresh);
    };
  }, [column, params.api]);

  const isDerived = origin === 'derived';
  const isViewDerived = origin === 'view';
  const isSystemField = !!params.systemFamily;
  const isNative = !!actions?.isNative;
  // ⚠ `!isSystemField` is load-bearing, not defensive. `origin === 'system'` is in the canEdit set
  // because that is what a stored opportunity `name` field def carries — and since W5's §9.2 flip a
  // system-field column carries it too, so without this guard the header would offer "Edit field"
  // (rename) and, on a native list, put a computed column one click from a rename that has nothing
  // to rename: `update_field` reads `crm_field_def`, where a system field has no row at all.
  const canEdit = isNative && !isSystemField
    && (origin === 'native' || origin === 'template' || origin === 'system');
  const canDelete = isNative && !isSystemField && (origin === 'native' || origin === 'template');
  const canInsert = isNative && !isViewDerived;
  const pinned = column.getPinned(); // 'left' | 'right' | null
  const lockPinned = !!column.getColDef().lockPinned; // the identity name column — no pin toggle

  const close = () => setMenuAnchor(null);
  // multiSort=false REPLACES the sort (this column becomes the sole key); multiSort=true ACCUMULATES
  // (AG assigns the next sortIndex). Clearing this column uses multiSort=true so other keys survive.
  const applySort = (dir: 'asc' | 'desc' | null, multi = false) => { params.setSort(dir, multi); close(); };
  const openFilter = () => { params.api.showColumnFilter(fieldKey); close(); };
  const togglePin = () => {
    params.api.applyColumnState({ state: [{ colId: fieldKey, pinned: pinned ? null : 'left' }] });
    close();
  };

  return (
    <div className={styles.colHeader}>
      <button
        type="button"
        className={styles.colHeaderLabel}
        title="Field details"
        onClick={(e) => setExplainAnchor(e.currentTarget)}
      >
        <span className={styles.colHeaderName}>{displayName}</span>
        {sort && (
          <span className={styles.colHeaderSort}>
            {sort === 'asc' ? '↑' : '↓'}
            {/* the precedence number only when >1 column is sorted (single-sort shows just the arrow) */}
            {multiActive && sortIndex != null && (
              <sup className={styles.colHeaderSortRank}>{sortIndex + 1}</sup>
            )}
          </span>
        )}
      </button>
      <button
        type="button"
        className={styles.colHeaderMenuBtn}
        aria-label={`${displayName} column menu`}
        onClick={(e) => { e.stopPropagation(); setMenuAnchor(menuAnchor ? null : e.currentTarget); }}
      >⋮</button>

      {explainAnchor && (
        <CrmPopover anchorEl={explainAnchor} onClose={() => setExplainAnchor(null)}>
          <div className={styles.colExplainer}>
            <div className={styles.colExplainerTitle}>{displayName}</div>
            <div className={styles.colExplainerType}>{FIELD_TYPE_EXPLAINERS[fieldType] ?? fieldType}</div>
            {/* §7.4: the definition tooltip release one owes every system field — "the field
                definition, the resolution strategy, and a statement that visible dates may be based
                on governed metadata". The header explainer already existed as the place a user asks
                "what IS this column?", so the sentence lands here rather than in a second surface. */}
            {params.definition && (
              <div className={styles.colExplainerDef}>{params.definition}</div>
            )}
            <span className={styles.colExplainerChip}>
              {isSystemField
                ? `System · ${SYSTEM_FAMILY_LABELS[params.systemFamily ?? ''] ?? 'computed'}`
                : isDerived ? 'Graph-derived' : isViewDerived ? 'View-derived' : 'List specific'}
            </span>
          </div>
        </CrmPopover>
      )}

      {menuAnchor && (
        <CrmPopover anchorEl={menuAnchor} onClose={close} align="right">
          {canEdit && (
            <button role="menuitem" className={styles.popoverItem}
                    onClick={() => { close(); actions?.openFieldEdit({ fieldKey, label: displayName, fieldType, origin }); }}>Edit field</button>
          )}
          <button role="menuitem" className={styles.popoverItem} onClick={() => applySort('asc')}>Sort A → Z</button>
          <button role="menuitem" className={styles.popoverItem} onClick={() => applySort('desc')}>Sort Z → A</button>
          {/* Item A: accumulate this column onto the existing sort (as a tie-breaker) — shown only
              when another column is already sorted, so it isn't clutter for the first sort. */}
          {otherSorted && (
            <>
              <button role="menuitem" className={styles.popoverItem} onClick={() => applySort('asc', true)}>Add to sort A → Z</button>
              <button role="menuitem" className={styles.popoverItem} onClick={() => applySort('desc', true)}>Add to sort Z → A</button>
            </>
          )}
          {sort && (
            <button role="menuitem" className={styles.popoverItem} onClick={() => applySort(null, true)}>Clear sort</button>
          )}
          <button role="menuitem" className={styles.popoverItem} onClick={openFilter}>Filter…</button>
          {!lockPinned && (
            <button role="menuitem" className={styles.popoverItem} onClick={togglePin}>
              {pinned ? 'Unpin column' : 'Pin column'}
            </button>
          )}
          {canInsert && (
            <>
              <button role="menuitem" className={styles.popoverItem}
                      onClick={() => { close(); actions?.insertColumn(fieldKey, 'left'); }}>Insert column left</button>
              <button role="menuitem" className={styles.popoverItem}
                      onClick={() => { close(); actions?.insertColumn(fieldKey, 'right'); }}>Insert column right</button>
            </>
          )}
          {!lockPinned && (
            <button role="menuitem" className={styles.popoverItem}
                    onClick={() => { close(); actions?.hideColumn(fieldKey); }}>Hide column</button>
          )}
          {canDelete && (
            <button role="menuitem" className={`${styles.popoverItem} ${styles.popoverDanger}`}
                    onClick={() => { close(); actions?.deleteField(fieldKey, displayName); }}>Delete field</button>
          )}
        </CrmPopover>
      )}
    </div>
  );
}

/** Pure helper (unit-tested): the column order after moving `movedId` adjacent to `anchorId`.
 * Returns a NEW ordered field_key array; a no-op (returns input) if either id is missing. */
export function reorderColIds(order: string[], movedId: string, anchorId: string, side: 'left' | 'right'): string[] {
  if (!order.includes(movedId) || !order.includes(anchorId) || movedId === anchorId) return order;
  const without = order.filter((c) => c !== movedId);
  const anchorIdx = without.indexOf(anchorId);
  const insertAt = side === 'left' ? anchorIdx : anchorIdx + 1;
  return [...without.slice(0, insertAt), movedId, ...without.slice(insertAt)];
}
